/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.dev/license
 */

/**
 * VENDORED FORK of `@angular/youtube-player`.
 *
 * - Source: `@angular/youtube-player` (Google LLC, MIT). Copied 2025-03-20
 *   (commit cd68caa "copy youtube player sources"). The npm package is NOT a
 *   dependency — it was uninstalled (see roadmap item 12, "YouTube player
 *   overhaul — Complete").
 * - Vendored against Angular 21 (workspace pins `@angular/core ^21.2.7`;
 *   currently resolves to 21.2.9).
 *
 * WHY THIS IS A REAL FORK (do not just re-install upstream):
 * The official component has not caught up to the customizations made here.
 * This fork deliberately diverges to:
 *   - use signal inputs / outputs (`input()`, `output()`, `linkedSignal`)
 *     instead of the upstream decorator-based `@Input`/`@Output` API
 *     (commits 9fb58cf, b7b6ce3);
 *   - lazy-load the IFrame API behind an `NgOptimizedImage`-based placeholder
 *     (`youtube-player-placeholder.ts`) with custom `webpImage` /
 *     `placeholderPriority` inputs and `i.ytimg.com` thumbnail sourcing
 *     (commits dffcef8, 5c9e4d5) — upstream's placeholder uses a plain `<img>`;
 *   - load the IFrame API script via `safevalues.setScriptSrc()` so it passes
 *     the site's strict hash-based CSP (allowlist lives in `public/_headers`,
 *     the source of truth; see docs/security-hardening-checklist.md).
 *
 * MAINTENANCE GUIDANCE FOR FUTURE EDITORS:
 *   - This is intentionally forked. Re-vendoring or de-vendoring back to the
 *     upstream npm package is a separate, larger task — only do it once
 *     upstream ships signal I/O + an NgOptimizedImage placeholder, otherwise
 *     these customizations regress.
 *   - On an Angular major bump, diff this file against the matching upstream
 *     tag (github.com/angular/components, `src/youtube-player/`) and re-port any
 *     breaking changes by hand, preserving the customizations listed above.
 *   - The scattered `eslint-disable-next-line` comments and the `as unknown`
 *     casts in the sibling `fake-youtube-player.ts` mirror upstream source and
 *     are acceptable in a vendored file; clean them up only if we de-vendor.
 */

/// <reference types="youtube" preserve="true" />

import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  CSP_NONCE,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  InjectionToken,
  NgZone,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  inject,
  input,
  linkedSignal,
  numberAttribute,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  outputFromObservable,
  takeUntilDestroyed,
  toObservable,
} from '@angular/core/rxjs-interop';
import {
  Observable,
  fromEventPattern,
  of as observableOf,
  switchMap,
} from 'rxjs';
import { trustedResourceUrl } from 'safevalues';
import { setScriptSrc } from 'safevalues/dom';
import {
  PlaceholderImageQuality,
  YouTubePlayerPlaceholder,
} from './youtube-player-placeholder';

declare global {
  interface Window {
    YT?: typeof YT | undefined;
    onYouTubeIframeAPIReady: (() => void) | undefined;
  }
}

/** Injection token used to configure the `YouTubePlayer`. */
export const YOUTUBE_PLAYER_CONFIG = new InjectionToken<YouTubePlayerConfig>(
  'YOUTUBE_PLAYER_CONFIG',
);

/** Object that can be used to configure the `YouTubePlayer`. */
export interface YouTubePlayerConfig {
  /** Whether to load the YouTube iframe API automatically. Defaults to `true`. */
  loadApi?: boolean;

  /**
   * By default the player shows a placeholder image instead of loading the YouTube API which
   * improves the initial page load performance. Use this option to disable the placeholder loading
   * behavior globally. Defaults to `false`.
   */
  disablePlaceholder?: boolean;

  /** Accessible label for the play button inside of the placeholder. */
  placeholderButtonLabel?: string;

  /**
   * Quality of the displayed placeholder image. Defaults to `standard`,
   * because not all video have a high-quality placeholder.
   */
  placeholderImageQuality?: PlaceholderImageQuality;
}

export const DEFAULT_PLAYER_WIDTH = 640;
export const DEFAULT_PLAYER_HEIGHT = 360;

/**
 * Object used to store the state of the player if the
 * user tries to interact with the API before it has been loaded.
 */
interface PendingPlayerState {
  playbackState?: PlayerState.PLAYING | PlayerState.PAUSED | PlayerState.CUED;
  playbackRate?: number;
  volume?: number;
  muted?: boolean;
  seek?: { seconds: number; allowSeekAhead: boolean };
}

/** Coercion function for time values. */
function coerceTime(value: number | undefined): number | undefined {
  return value == null ? value : numberAttribute(value, 0);
}

/**
 * Equivalent of `YT.PlayerState` which we can't use, because it's meant to
 * be read off the `window` which we can't do before the API has been loaded.
 */
enum PlayerState {
  UNSTARTED = -1,
  ENDED = 0,
  PLAYING = 1,
  PAUSED = 2,
  BUFFERING = 3,
  CUED = 5,
}

/**
 * Angular component that renders a YouTube player via the YouTube player
 * iframe API.
 * @see https://developers.google.com/youtube/iframe_api_reference
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- vendored fork of @angular/youtube-player; keeps upstream's real `<youtube-player>` tag name rather than an app- prefix, see file header
  selector: 'youtube-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [YouTubePlayerPlaceholder],
  styleUrl: 'youtube-player.scss',
  template: `
    @let currentVideoId = videoId();
    @let shouldShowPlaceholder = _shouldShowPlaceholder();
    @if (shouldShowPlaceholder && currentVideoId) {
      <youtube-player-placeholder
        [videoId]="currentVideoId"
        [width]="width()"
        [height]="height()"
        [isLoading]="_isLoading()"
        [buttonLabel]="_placeholderButtonLabel()"
        [quality]="_placeholderImageQuality()"
        [webpImage]="placeholderWebpImage()"
        [placeholderPriority]="placeholderPriority()"
        (click)="_load(true)"
      />
    }
    <div [style.display]="shouldShowPlaceholder ? 'none' : ''">
      <div #youtubeContainer></div>
    </div>
  `,
})
export class YouTubePlayer implements AfterViewInit, OnChanges, OnDestroy {
  private _ngZone = inject(NgZone);
  private readonly _nonce = inject(CSP_NONCE, { optional: true });
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private _player = signal<YT.Player | undefined>(undefined);
  private _pendingPlayer = signal<YT.Player | undefined>(undefined);
  private _existingApiReadyCallback = signal<(() => void) | undefined>(
    undefined,
  );
  private _pendingPlayerState = signal<PendingPlayerState | undefined>(
    undefined,
  );
  private _destroyRef = inject(DestroyRef);
  private readonly _playerChanges = signal<YT.Player | undefined>(undefined);
  protected _isLoading = signal(false);
  protected _hasPlaceholder = signal(true);

  /** Whether we're currently rendering inside a browser. */
  private readonly _isBrowser = signal(false);

  /** YouTube Video ID to view */
  videoId = input<string | undefined>('');

  /** Height of video player */
  height = input(DEFAULT_PLAYER_HEIGHT, {
    transform: (value) => numberAttribute(value, DEFAULT_PLAYER_HEIGHT),
  });

  /** Width of video player */
  width = input(DEFAULT_PLAYER_WIDTH, {
    transform: (value) => numberAttribute(value, DEFAULT_PLAYER_WIDTH),
  });

  /** The moment when the player is supposed to start playing */
  startSeconds = input(0, { transform: coerceTime });

  /** The moment when the player is supposed to stop playing */
  endSeconds = input(0, { transform: coerceTime });

  /** The suggested quality of the player */
  suggestedQuality = input<YT.SuggestedVideoQuality | undefined>(undefined);

  /**
   * Extra parameters used to configure the player. See:
   * https://developers.google.com/youtube/player_parameters.html?playerVersion=HTML5#Parameters
   */
  playerVars = input<YT.PlayerVars | undefined>(undefined);

  /** Whether cookies inside the player have been disabled. */
  disableCookies = input(false, { transform: booleanAttribute });

  /** Whether to automatically load the YouTube iframe API. Defaults to `true`. */
  loadApi = input(true, { transform: booleanAttribute });
  private _loadApi = linkedSignal(() => this.loadApi());

  /**
   * By default the player shows a placeholder image instead of loading the YouTube API which
   * improves the initial page load performance. This input allows for the behavior to be disabled.
   */
  disablePlaceholder = input(false, { transform: booleanAttribute });
  private _disablePlaceholder = linkedSignal(() => this.disablePlaceholder());

  /**
   * Whether the iframe will attempt to load regardless of the status of the api on the
   * page. Set this to true if you don't want the `onYouTubeIframeAPIReady` field to be
   * set on the global window.
   */
  showBeforeIframeApiLoads = input(false, { transform: booleanAttribute });

  /** Accessible label for the play button inside of the placeholder. */
  placeholderButtonLabel = input('');
  protected _placeholderButtonLabel = linkedSignal(() =>
    this.placeholderButtonLabel(),
  );

  /**
   * Quality of the displayed placeholder image. Defaults to `standard`,
   * because not all video have a high-quality placeholder.
   */
  playlistPlaceholderVideoId = input<string>();
  protected placeholderVideoId = computed(() => {
    return this.videoId() ?? this.playlistPlaceholderVideoId();
  });

  placeholderWebpImage = input(true, { transform: booleanAttribute });
  placeholderPriority = input(false, { transform: booleanAttribute });
  placeholderImageQuality = input<PlaceholderImageQuality>('standard');
  protected _placeholderImageQuality = linkedSignal(() =>
    this.placeholderImageQuality(),
  );

  // Note: ready event can't go through the lazy emitter, because it
  // happens before the `_playerChanges` stream emits the new player.

  /** Emits when the player is initialized. */
  readonly ready = output<YT.PlayerEvent>();

  /** Emits when the state of the player has changed. */
  readonly stateChange = outputFromObservable(
    this._getLazyEmitter<YT.OnStateChangeEvent>('onStateChange'),
  );

  /** Emits when there's an error while initializing the player. */
  readonly error = outputFromObservable(
    this._getLazyEmitter<YT.OnErrorEvent>('onError'),
  );

  /** Emits when the underlying API of the player has changed. */
  readonly apiChange = outputFromObservable(
    this._getLazyEmitter<YT.PlayerEvent>('onApiChange'),
  );

  /** Emits when the playback quality has changed. */
  readonly playbackQualityChange = outputFromObservable(
    this._getLazyEmitter<YT.OnPlaybackQualityChangeEvent>(
      'onPlaybackQualityChange',
    ),
  );

  /** Emits when the playback rate has changed. */
  readonly playbackRateChange = outputFromObservable(
    this._getLazyEmitter<YT.OnPlaybackRateChangeEvent>('onPlaybackRateChange'),
  );

  /** The element that will be replaced by the iframe. */
  youtubeContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('youtubeContainer');
  private _youtubeContainerElement = computed(
    () => this.youtubeContainer().nativeElement,
  );

  constructor() {
    const platformId = inject(PLATFORM_ID);
    const config = inject(YOUTUBE_PLAYER_CONFIG, { optional: true });
    this._loadApi.set(config?.loadApi ?? true);
    this._disablePlaceholder.set(!!config?.disablePlaceholder);
    const { placeholderButtonLabel = '', placeholderImageQuality = '' } =
      config ?? {};
    this._placeholderButtonLabel.set(placeholderButtonLabel || 'Play video');
    this._placeholderImageQuality.set(placeholderImageQuality || 'standard');
    this._isBrowser.set(isPlatformBrowser(platformId));
  }

  ngAfterViewInit() {
    this._conditionallyLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this._shouldRecreatePlayer(changes)) {
      this._conditionallyLoad();
    } else if (this._player()) {
      if (changes['width'] || changes['height']) {
        this._setSize();
      }

      if (changes['suggestedQuality']) {
        this._setQuality();
      }

      if (
        changes['startSeconds'] ||
        changes['endSeconds'] ||
        changes['suggestedQuality']
      ) {
        this._cuePlayer();
      }
    }
  }

  ngOnDestroy() {
    this._pendingPlayer()?.destroy();

    const player = this._player();
    if (player) {
      player.destroy();
      window.onYouTubeIframeAPIReady = this._existingApiReadyCallback();
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#playVideo */
  playVideo() {
    const player = this._player();
    if (player) {
      player.playVideo();
    } else {
      this._getPendingState().playbackState = PlayerState.PLAYING;
      this._load(true);
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#pauseVideo */
  pauseVideo() {
    const player = this._player();
    if (player) {
      player.pauseVideo();
    } else {
      this._getPendingState().playbackState = PlayerState.PAUSED;
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#stopVideo */
  stopVideo() {
    const player = this._player();
    if (player) {
      player.stopVideo();
    } else {
      // It seems like YouTube sets the player to CUED when it's stopped.
      this._getPendingState().playbackState = PlayerState.CUED;
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#seekTo */
  seekTo(seconds: number, allowSeekAhead: boolean) {
    const player = this._player();
    if (player) {
      player.seekTo(seconds, allowSeekAhead);
    } else {
      this._getPendingState().seek = { seconds, allowSeekAhead };
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#mute */
  mute() {
    const player = this._player();
    if (player) {
      player.mute();
    } else {
      this._getPendingState().muted = true;
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#unMute */
  unMute() {
    const player = this._player();
    if (player) {
      player.unMute();
    } else {
      this._getPendingState().muted = false;
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#isMuted */
  isMuted(): boolean {
    const player = this._player();
    if (player) {
      return player.isMuted();
    }

    const pendingPlayerState = this._pendingPlayerState();
    if (pendingPlayerState) {
      return !!pendingPlayerState.muted;
    }

    return false;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#setVolume */
  setVolume(volume: number) {
    const player = this._player();
    if (player) {
      player.setVolume(volume);
    } else {
      this._getPendingState().volume = volume;
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getVolume */
  getVolume(): number {
    const player = this._player();
    if (player) {
      return player.getVolume();
    }

    const pendingPlayerState = this._pendingPlayerState();
    if (pendingPlayerState?.volume != null) {
      return pendingPlayerState.volume;
    }

    return 0;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#setPlaybackRate */
  setPlaybackRate(playbackRate: number) {
    const player = this._player();
    if (player) {
      player.setPlaybackRate(playbackRate);
    } else {
      this._getPendingState().playbackRate = playbackRate;
    }
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getPlaybackRate */
  getPlaybackRate(): number {
    const player = this._player();
    if (player) {
      return player.getPlaybackRate();
    }

    const pendingPlayerState = this._pendingPlayerState();
    if (pendingPlayerState?.playbackRate != null) {
      return pendingPlayerState.playbackRate;
    }

    return 0;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getAvailablePlaybackRates */
  getAvailablePlaybackRates(): number[] {
    const player = this._player();
    return player ? player.getAvailablePlaybackRates() : [];
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getVideoLoadedFraction */
  getVideoLoadedFraction(): number {
    const player = this._player();
    return player ? player.getVideoLoadedFraction() : 0;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getPlayerState */
  getPlayerState(): YT.PlayerState | undefined {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the ambient `youtube` types declare window.YT as always-defined, but it's genuinely undefined until the IFrame API script loads; vendored from upstream, see file header
    if (!this._isBrowser() || !window.YT) {
      return undefined;
    }

    const player = this._player();
    if (player) {
      return player.getPlayerState();
    }

    const pendingPlayerState = this._pendingPlayerState();
    if (pendingPlayerState?.playbackState != null) {
      return pendingPlayerState.playbackState;
    }

    return PlayerState.UNSTARTED;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getCurrentTime */
  getCurrentTime(): number {
    const player = this._player();
    if (player) {
      return player.getCurrentTime();
    }

    const pendingPlayerState = this._pendingPlayerState();
    if (pendingPlayerState?.seek) {
      return pendingPlayerState.seek.seconds;
    }

    return 0;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getPlaybackQuality */
  getPlaybackQuality(): YT.SuggestedVideoQuality {
    const player = this._player();
    return player ? player.getPlaybackQuality() : 'default';
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getAvailableQualityLevels */
  getAvailableQualityLevels(): YT.SuggestedVideoQuality[] {
    const player = this._player();
    return player ? player.getAvailableQualityLevels() : [];
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getDuration */
  getDuration(): number {
    const player = this._player();
    return player ? player.getDuration() : 0;
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getVideoUrl */
  getVideoUrl(): string {
    const player = this._player();
    return player ? player.getVideoUrl() : '';
  }

  /** See https://developers.google.com/youtube/iframe_api_reference#getVideoEmbedCode */
  getVideoEmbedCode(): string {
    const player = this._player();
    return player ? player.getVideoEmbedCode() : '';
  }

  /**
   * Attempts to put the player into fullscreen mode, depending on browser support.
   * @param options Options controlling how the element behaves in fullscreen mode.
   */
  async requestFullscreen(options?: FullscreenOptions): Promise<void> {
    // Note that we do this on the host, rather than the iframe, because it allows us to handle the
    // placeholder in fullscreen mode. Null check the method since it's not supported everywhere.
    const element = this._elementRef.nativeElement;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- DOM lib types declare requestFullscreen as always present on Element, but support is real-world inconsistent (see comment above); vendored from upstream, see file header
    return element.requestFullscreen
      ? element.requestFullscreen(options)
      : Promise.reject(new Error('Fullscreen API not supported by browser.'));
  }

  /**
   * Loads the YouTube API and sets up the player.
   * @param playVideo Whether to automatically play the video once the player is loaded.
   */
  protected _load(playVideo: boolean) {
    // Don't do anything if we're not in a browser environment.
    if (!this._isBrowser()) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- same window.YT-always-defined type gap as getPlayerState above; vendored from upstream, see file header
    if (!window.YT?.Player) {
      if (this._loadApi()) {
        this._isLoading.set(true);
        loadApi(this._nonce);
      } else if (
        this.showBeforeIframeApiLoads() &&
        (typeof ngDevMode === 'undefined' || ngDevMode)
      ) {
        throw new Error(
          'Namespace YT not found, cannot construct embedded youtube player. ' +
            'Please install the YouTube Player API Reference for iframe Embeds: ' +
            'https://developers.google.com/youtube/iframe_api_reference',
        );
      }

      this._existingApiReadyCallback.set(window.onYouTubeIframeAPIReady);

      window.onYouTubeIframeAPIReady = () => {
        this._existingApiReadyCallback()?.();
        this._ngZone.run(() => {
          this._createPlayer(playVideo);
        });
      };
    } else {
      this._createPlayer(playVideo);
    }
  }

  /** Loads the player depending on the internal state of the component. */
  private _conditionallyLoad() {
    // If the placeholder isn't shown anymore, we have to trigger a load.
    if (!this._shouldShowPlaceholder()) {
      this._load(false);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- YT.PlayerVars['autoplay'] is typed as the ambient YT.AutoPlay enum; comparing it to the raw literal 1 (upstream's own pattern) trips this rule. Vendored from upstream, see file header
    } else if (this.playerVars()?.autoplay === 1) {
      // If it's an autoplaying video, we have to hide the placeholder and start playing.
      this._load(true);
    }
  }

  /** Whether to show the placeholder element. */
  protected _shouldShowPlaceholder = computed(() => {
    if (this._disablePlaceholder()) {
      return false;
    }

    // Since we don't load the API on the server, we show the placeholder permanently.
    if (!this._isBrowser()) {
      return true;
    }

    return this._hasPlaceholder() && !!this.videoId() && !this._player();
  });

  /** Gets an object that should be used to store the temporary API state. */
  private _getPendingState(): PendingPlayerState {
    const pendingPlayerState = this._pendingPlayerState();
    if (!pendingPlayerState) {
      const newState = {};
      this._pendingPlayerState.set(newState);
      return newState;
    }

    return pendingPlayerState;
  }

  /**
   * Determines whether a change in the component state
   * requires the YouTube player to be recreated.
   */
  private _shouldRecreatePlayer(changes: SimpleChanges): boolean {
    const change =
      changes['videoId'] ??
      changes['playerVars'] ??
      changes['disableCookies'] ??
      changes['disablePlaceholder'];
    return !!change && !change.isFirstChange();
  }

  /**
   * Creates a new YouTube player and destroys the existing one.
   * @param playVideo Whether to play the video once it loads.
   */
  private _createPlayer(playVideo: boolean) {
    this._player()?.destroy();
    this._pendingPlayer()?.destroy();

    // A player can't be created if the API isn't loaded,
    // or there isn't a video or playlist to be played.
    if (
      typeof YT === 'undefined' ||
      (!this.videoId() && !this.playerVars()?.list)
    ) {
      return;
    }

    // Important! We need to create the Player object outside of the `NgZone`, because it kicks
    // off a 250ms setInterval which will continually trigger change detection if we don't.
    const params: YT.PlayerOptions = {
      host: this.disableCookies()
        ? 'https://www.youtube-nocookie.com'
        : undefined,
      width: this.width(),
      height: this.height(),
      // Calling `playVideo` on load doesn't appear to actually play
      // the video so we need to trigger it through `playerVars` instead.
      playerVars: playVideo
        ? { ...(this.playerVars() ?? {}), autoplay: 1 }
        : this.playerVars(),
    };
    // We only want to inject a videoId if one is provided, otherwise loading a playlist via
    // playerVars.list, the missing videoId will create a null value in the youtube iframe url
    // and that can trigger a JS error `Invalid video id` in widget api.
    if (this.videoId()) {
      params.videoId = this.videoId();
    }
    const player = this._ngZone.runOutsideAngular(
      () => new YT.Player(this._youtubeContainerElement(), params),
    );

    const whenReady = (event: YT.PlayerEvent) => {
      // Only assign the player once it's ready, otherwise YouTube doesn't expose some APIs.
      this._ngZone.run(() => {
        this._isLoading.set(false);
        this._hasPlaceholder.set(false);
        this._player.set(player);
        this._pendingPlayer.set(undefined);
        player.removeEventListener('onReady', whenReady);
        this._playerChanges.set(player);
        this.ready.emit(event);
        this._setSize();
        this._setQuality();

        const pendingPlayerState = this._pendingPlayerState();
        if (pendingPlayerState) {
          this._applyPendingPlayerState(player, pendingPlayerState);
          this._pendingPlayerState.set(undefined);
        }

        // Only cue the player when it either hasn't started yet or it's cued,
        // otherwise cuing it can interrupt a player with autoplay enabled.
        const state = player.getPlayerState();
        const startSeconds = this.startSeconds();
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- state is the ambient YT.PlayerState enum; PlayerState (line 150) is this file's local re-declaration of the same values (see its doc comment). Vendored from upstream, see file header
          state === PlayerState.UNSTARTED ||
          // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- same cross-enum comparison as above
          state === PlayerState.CUED ||
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- getPlayerState()'s return type doesn't include null/undefined, but the runtime API can genuinely return it before ready; vendored from upstream, see file header
          state == null
        ) {
          this._cuePlayer();
        } else if (playVideo && startSeconds && startSeconds > 0) {
          // We have to use `seekTo` when `startSeconds` are specified to simulate it playing from
          // a specific time. The "proper" way to do it would be to either go through `cueVideoById`
          // or `playerVars.start`, but at the time of writing both end up resetting the video
          // to the state as if the user hasn't interacted with it.
          player.seekTo(startSeconds, true);
        }

        this._changeDetectorRef.markForCheck();
      });
    };

    this._pendingPlayer.set(player);
    player.addEventListener('onReady', whenReady);
  }

  /** Applies any state that changed before the player was initialized. */
  private _applyPendingPlayerState(
    player: YT.Player,
    pendingState: PendingPlayerState,
  ): void {
    const { playbackState, playbackRate, volume, muted, seek } = pendingState;

    switch (playbackState) {
      case PlayerState.PLAYING:
        player.playVideo();
        break;
      case PlayerState.PAUSED:
        player.pauseVideo();
        break;
      case PlayerState.CUED:
        player.stopVideo();
        break;
    }

    if (playbackRate != null) {
      player.setPlaybackRate(playbackRate);
    }

    if (volume != null) {
      player.setVolume(volume);
    }

    if (muted != null) {
      if (muted) {
        player.mute();
      } else {
        player.unMute();
      }
    }

    if (seek != null) {
      player.seekTo(seek.seconds, seek.allowSeekAhead);
    }
  }

  /** Cues the player based on the current component state. */
  private _cuePlayer() {
    const videoId = this.videoId();
    const startSeconds = this.startSeconds();
    const endSeconds = this.endSeconds();
    const suggestedQuality = this.suggestedQuality();
    const player = this._player();
    if (player && videoId) {
      player.cueVideoById({
        videoId,
        startSeconds,
        endSeconds,
        suggestedQuality,
      });
    }
  }

  /** Sets the player's size based on the current input values. */
  private _setSize() {
    this._player()?.setSize(this.width(), this.height());
  }

  /** Sets the player's quality based on the current input values. */
  private _setQuality() {
    const suggestedQuality = this.suggestedQuality();
    const player = this._player();
    if (player && suggestedQuality) {
      player.setPlaybackQuality(suggestedQuality);
    }
  }

  /** Gets an observable that adds an event listener to the player when a user subscribes to it. */
  private _getLazyEmitter<T extends YT.PlayerEvent>(
    name: keyof YT.Events,
  ): Observable<T> {
    // Start with the stream of players. This way the events will be transferred
    // over to the new player if it gets swapped out under-the-hood.
    return toObservable(this._playerChanges).pipe(
      // Switch to the bound event. `switchMap` ensures that the old event is removed when the
      // player is changed. If there's no player, return an observable that never emits.
      switchMap((player) => {
        return player
          ? fromEventPattern<T>(
              (listener) => {
                player.addEventListener(name, listener);
              },
              (listener) => {
                // The API seems to throw when we try to unbind from a destroyed player and it
                // doesn'texpose whether the player has been destroyed so we have to wrap it in a
                // try/catch to prevent the entire stream from erroring out.
                try {
                  player.removeEventListener(name, listener);
                } catch {
                  /* empty */
                }
              },
            )
          : observableOf();
      }),
      // By default we run all the API interactions outside the zone
      // so we have to bring the events back in manually when they emit.
      (source) =>
        new Observable<T>((observer) =>
          source.subscribe({
            next: (value) => {
              this._ngZone.run(() => {
                observer.next(value);
              });
            },
            error: (error) => {
              observer.error(error);
            },
            complete: () => {
              observer.complete();
            },
          }),
        ),
      // Ensures that everything is cleared out on destroy.
      takeUntilDestroyed(this._destroyRef),
    );
  }
}

let apiLoaded = false;

/** Loads the YouTube API from a specified URL only once. */
function loadApi(nonce: string | null): void {
  if (apiLoaded) {
    return;
  }

  // We can use `document` directly here, because this logic doesn't run outside the browser.
  const url = trustedResourceUrl`https://www.youtube.com/iframe_api`;
  const script = document.createElement('script');
  const callback = (event: Event) => {
    script.removeEventListener('load', callback);
    script.removeEventListener('error', callback);

    if (event.type === 'error') {
      apiLoaded = false;

      if (typeof ngDevMode === 'undefined' || ngDevMode) {
        console.error(`Failed to load YouTube API from ${url.toString()}`);
      }
    }
  };
  script.addEventListener('load', callback);
  script.addEventListener('error', callback);
  setScriptSrc(script, url);
  script.async = true;

  if (nonce) {
    script.setAttribute('nonce', nonce);
  }

  // Set this immediately to true so we don't start loading another script
  // while this one is pending. If loading fails, we'll flip it back to false.
  apiLoaded = true;
  document.body.appendChild(script);
}
